import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  ChevronLeft,
  ChevronRight,
  ArrowUpFromLine,
  CheckCircle2,
  XCircle,
  Clock,
  Send,
  X,
  Loader2,
  Filter,
  AlertTriangle,
  Shield,
  Banknote,
} from "lucide-react";
import { trustApi, clientsApi, mattersApi, PaginatedResponse } from "../../services/api";
import SearchableSelect, { SelectOption } from "../ui/SearchableSelect";
import PremiumModal from "../ui/PremiumModal";
import ConfirmDialog from "../ui/ConfirmDialog";

interface Props {
  bankAccountId: string;
  onRefresh: () => void;
}

interface Disbursement {
  _id: string;
  clientId: { _id: string; displayName: string } | string;
  matterId: { _id: string; title: string } | string;
  payee: { name: string; kind: string };
  amountCents: number;
  currency: string;
  purpose: string;
  status: string;
  requestedAt: string;
  approvals: { status: string; by: any; at: string; note: string | null }[];
  routing: { method: string };
}

const statusConfig: Record<string, { label: string; bg: string; text: string; Icon: React.FC<any> }> = {
  draft: { label: "Draft", bg: "bg-slate-100", text: "text-slate-600", Icon: Clock },
  pending_approval: { label: "Pending Approval", bg: "bg-amber-100", text: "text-amber-700", Icon: Clock },
  approved: { label: "Approved", bg: "bg-blue-100", text: "text-blue-700", Icon: CheckCircle2 },
  released: { label: "Released", bg: "bg-emerald-100", text: "text-emerald-700", Icon: Banknote },
  void: { label: "Void", bg: "bg-red-100", text: "text-red-700", Icon: XCircle },
};

function formatCents(cents: number): string {
  return new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(cents / 100);
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
}

const TrustDisbursementsTab: React.FC<Props> = ({ bankAccountId, onRefresh }) => {
  const [disbursements, setDisbursements] = useState<Disbursement[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Create form
  const [clients, setClients] = useState<any[]>([]);
  const [matters, setMatters] = useState<any[]>([]);
  const [availableBalance, setAvailableBalance] = useState<number | null>(null);
  // True when the balance lookup failed, so we don't show a misleading $0
  const [balanceLoadFailed, setBalanceLoadFailed] = useState(false);
  const [releaseTarget, setReleaseTarget] = useState<Disbursement | null>(null);
  const [form, setForm] = useState({
    clientId: "",
    matterId: "",
    amountCents: "",
    purpose: "",
    payeeName: "",
    payeeKind: "client" as string,
    routingMethod: "eft" as string,
  });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const fetchDisbursements = useCallback(async () => {
    if (!bankAccountId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params: Record<string, any> = {
        bankAccountId,
        page: pagination.page,
        limit: pagination.limit,
      };
      if (statusFilter) params.status = statusFilter;

      const res = await trustApi.listDisbursements(params);
      const data = res.data as PaginatedResponse<Disbursement>;
      setDisbursements(data.data || []);
      setPagination((prev) => ({ ...prev, ...data.pagination }));
    } catch (err) {
      console.error("Failed to fetch disbursements:", err);
    } finally {
      setLoading(false);
    }
  }, [bankAccountId, pagination.page, pagination.limit, statusFilter]);

  useEffect(() => {
    fetchDisbursements();
  }, [fetchDisbursements]);

  // Load clients for modal
  useEffect(() => {
    if (showCreateModal) {
      clientsApi.list({ limit: 100 }).then((res) => setClients(res.data?.data || []));
    }
  }, [showCreateModal]);

  // Load matters when client changes
  useEffect(() => {
    if (form.clientId) {
      mattersApi.list({ clientId: form.clientId, limit: 100 }).then((res) => setMatters(res.data?.data || []));
    } else {
      setMatters([]);
      setAvailableBalance(null);
      setBalanceLoadFailed(false);
    }
  }, [form.clientId]);

  // Load available balance when matter changes
  useEffect(() => {
    if (form.clientId && form.matterId && bankAccountId) {
      setBalanceLoadFailed(false);
      trustApi
        .getSubledger({ bankAccountId, clientId: form.clientId, matterId: form.matterId })
        .then((res) => {
          const entries = res.data?.data || [];
          if (entries.length > 0) {
            setAvailableBalance(entries[0].availableBalanceCents);
          } else {
            setAvailableBalance(0);
          }
        })
        .catch(() => {
          setAvailableBalance(null);
          setBalanceLoadFailed(true);
        });
    } else {
      setAvailableBalance(null);
      setBalanceLoadFailed(false);
    }
  }, [form.clientId, form.matterId, bankAccountId]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setCreateError("");
    try {
      const amountCents = Math.round(parseFloat(form.amountCents) * 100);
      if (isNaN(amountCents) || amountCents <= 0) {
        setCreateError("Please enter a valid amount");
        setCreating(false);
        return;
      }

      if (availableBalance !== null && amountCents > availableBalance) {
        setCreateError(`Amount exceeds available balance of ${formatCents(availableBalance)}`);
        setCreating(false);
        return;
      }

      await trustApi.createDisbursement({
        bankAccountId,
        clientId: form.clientId,
        matterId: form.matterId,
        amountCents,
        purpose: form.purpose,
        payee: {
          kind: form.payeeKind,
          name: form.payeeName,
          clientId: form.payeeKind === "client" ? form.clientId : undefined,
        },
        routing: { method: form.routingMethod },
      });

      setShowCreateModal(false);
      setForm({ clientId: "", matterId: "", amountCents: "", purpose: "", payeeName: "", payeeKind: "client", routingMethod: "eft" });
      fetchDisbursements();
      onRefresh();
    } catch (err: any) {
      setCreateError(err.response?.data?.error || "Failed to create disbursement");
    } finally {
      setCreating(false);
    }
  };

  const handleAction = async (id: string, action: string) => {
    setActionLoading(id);
    try {
      if (action === "submit") await trustApi.submitDisbursement(id);
      else if (action === "approve") await trustApi.approveDisbursement(id);
      else if (action === "reject") {
        const note = window.prompt("Rejection reason:");
        if (!note) { setActionLoading(null); return; }
        await trustApi.rejectDisbursement(id, note);
      } else if (action === "release") await trustApi.releaseDisbursement(id);
      else if (action === "void") {
        const reason = window.prompt("Void reason:");
        if (!reason) { setActionLoading(null); return; }
        await trustApi.voidDisbursement(id, reason);
      }
      fetchDisbursements();
      onRefresh();
    } catch (err: any) {
      setErrorMessage(err.response?.data?.error || `Failed to ${action}`);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div>
      <ConfirmDialog
        open={!!releaseTarget}
        title="Release Trust Funds"
        message={
          releaseTarget
            ? `Release ${formatCents(releaseTarget.amountCents)} from trust to ${releaseTarget.payee?.name || "the payee"}? This moves money out of the trust account.`
            : ""
        }
        confirmLabel="Release Funds"
        variant="warning"
        loading={!!releaseTarget && actionLoading === releaseTarget._id}
        onConfirm={async () => {
          if (!releaseTarget) return;
          await handleAction(releaseTarget._id, "release");
          setReleaseTarget(null);
        }}
        onCancel={() => setReleaseTarget(null)}
      />

      {/* Error Banner */}
      {errorMessage && (
        <div className="mb-4 flex items-center gap-2.5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span className="flex-1">{errorMessage}</span>
          <button onClick={() => setErrorMessage(null)} className="ml-auto text-red-400 hover:text-red-600 transition-colors">&times;</button>
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <SearchableSelect
            options={[
              { value: "draft", label: "Draft" },
              { value: "pending_approval", label: "Pending Approval" },
              { value: "approved", label: "Approved" },
              { value: "released", label: "Released" },
              { value: "void", label: "Void" },
            ]}
            value={statusFilter || null}
            onChange={(val) => { setStatusFilter(val || ""); setPagination((prev) => ({ ...prev, page: 1 })); }}
            placeholder="All Statuses"
            icon={Filter}
            clearable
          />
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          disabled={!bankAccountId}
          className="inline-flex items-center gap-2 px-5 py-2.5 text-white font-bold rounded-xl transition-colors disabled:opacity-50"
          style={{ backgroundColor: "var(--color-primary)" }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
        >
          <Plus className="h-4 w-4" />
          New Disbursement
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
          </div>
        ) : disbursements.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-500">
            <ArrowUpFromLine className="h-12 w-12 mb-3 text-slate-300" />
            <p className="font-medium text-lg">No trust disbursements</p>
            <p className="text-sm mt-1">Create a disbursement to release trust funds</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200" style={{ backgroundColor: "var(--color-primary)" }}>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">Date</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">Client</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden md:table-cell">Payee</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden lg:table-cell">Purpose</th>
                    <th className="text-right px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">Amount</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">Status</th>
                    <th className="text-right px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {disbursements.map((disb, i) => {
                    const clientName = typeof disb.clientId === "object" && disb.clientId ? disb.clientId.displayName : "Unknown";
                    const status = statusConfig[disb.status] || statusConfig.draft;
                    const StatusIcon = status.Icon;

                    return (
                      <motion.tr
                        key={disb._id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.02 }}
                        className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                      >
                        <td className="px-6 py-4 text-sm text-slate-600">{formatDate(disb.requestedAt)}</td>
                        <td className="px-6 py-4 text-sm font-medium text-slate-800">{clientName}</td>
                        <td className="px-6 py-4 text-sm text-slate-600 hidden md:table-cell">{disb.payee?.name || "-"}</td>
                        <td className="px-6 py-4 text-sm text-slate-600 hidden lg:table-cell truncate max-w-[200px]">{disb.purpose}</td>
                        <td className="px-6 py-4 text-right text-sm font-semibold text-slate-800">{formatCents(disb.amountCents)}</td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
                            <StatusIcon className="h-3 w-3" />
                            {status.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end gap-1.5 flex-wrap">
                            {disb.status === "draft" && (
                              <button
                                onClick={() => handleAction(disb._id, "submit")}
                                disabled={actionLoading === disb._id}
                                className="text-xs font-medium px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                              >
                                <Send className="h-3 w-3 inline mr-1" />Submit
                              </button>
                            )}
                            {disb.status === "pending_approval" && (
                              <>
                                <button
                                  onClick={() => handleAction(disb._id, "approve")}
                                  disabled={actionLoading === disb._id}
                                  className="text-xs font-medium px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                                >
                                  Approve
                                </button>
                                <button
                                  onClick={() => handleAction(disb._id, "reject")}
                                  disabled={actionLoading === disb._id}
                                  className="text-xs font-medium px-3 py-1.5 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50"
                                >
                                  Reject
                                </button>
                              </>
                            )}
                            {disb.status === "approved" && (
                              <button
                                onClick={() => setReleaseTarget(disb)}
                                disabled={actionLoading === disb._id}
                                className="text-xs font-medium px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                              >
                                <Banknote className="h-3 w-3 inline mr-1" />Release
                              </button>
                            )}
                            {!["void", "released"].includes(disb.status) && (
                              <button
                                onClick={() => handleAction(disb._id, "void")}
                                disabled={actionLoading === disb._id}
                                className="text-xs font-medium px-3 py-1.5 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50"
                              >
                                Void
                              </button>
                            )}
                          </div>
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {pagination.pages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
                <p className="text-sm text-slate-500">Page {pagination.page} of {pagination.pages} ({pagination.total} total)</p>
                <div className="flex gap-2">
                  <button disabled={pagination.page <= 1} onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))} className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button disabled={pagination.page >= pagination.pages} onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))} className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50">
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Disbursement Modal */}
      <PremiumModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="New Trust Disbursement"
        icon={ArrowUpFromLine}
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => setShowCreateModal(false)} className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
            <button
              type="submit"
              form="createDisbursementForm"
              disabled={creating || !form.clientId || !form.matterId || (availableBalance !== null && form.amountCents !== "" && parseFloat(form.amountCents) * 100 > availableBalance)}
              className="px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50"
              style={{ backgroundColor: "var(--color-primary)" }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
            >
              {creating ? "Creating..." : "Create Disbursement"}
            </button>
          </div>
        }
      >
        {createError && <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{createError}</div>}

        {/* Available balance display */}
        {balanceLoadFailed && (
          <div className="mb-4 p-3 rounded-lg flex items-center gap-2 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-700">
              Couldn't load the available trust balance. The server will still check it when you save.
            </span>
          </div>
        )}
        {availableBalance !== null && (
          <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${availableBalance > 0 ? "bg-emerald-50" : "bg-amber-50"}`}>
            <Shield className={`h-4 w-4 ${availableBalance > 0 ? "text-emerald-600" : "text-amber-600"}`} />
            <span className={`text-sm font-medium ${availableBalance > 0 ? "text-emerald-700" : "text-amber-700"}`}>
              Available balance: {formatCents(availableBalance)}
            </span>
          </div>
        )}

        <form id="createDisbursementForm" onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Client *</label>
            <SearchableSelect
              options={clients.map((c: any) => ({ value: c._id, label: c.displayName }))}
              value={form.clientId || null}
              onChange={(val) => {
                const selected = clients.find((c: any) => c._id === val);
                setForm({
                  ...form,
                  clientId: val || "",
                  matterId: "",
                  payeeName: form.payeeKind === "client" && selected ? selected.displayName : form.payeeName,
                });
              }}
              placeholder="Select client..."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Matter *</label>
            <SearchableSelect
              options={matters.map((m: any) => ({ value: m._id, label: m.title }))}
              value={form.matterId || null}
              onChange={(val) => setForm({ ...form, matterId: val || "" })}
              placeholder="Select matter..."
              required
              disabled={!form.clientId}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Amount (CAD) *</label>
            <input type="number" step="0.01" min="0.01" required value={form.amountCents} onChange={(e) => setForm({ ...form, amountCents: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600" placeholder="0.00" />
            {availableBalance !== null && form.amountCents && parseFloat(form.amountCents) * 100 > availableBalance && (
              <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                Exceeds available balance
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Purpose *</label>
            <input type="text" required value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600" placeholder="e.g. Land transfer tax payment" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Payee Name *</label>
            <input type="text" required value={form.payeeName} onChange={(e) => setForm({ ...form, payeeName: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600" placeholder="Who will receive the funds" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Payee Type</label>
              <SearchableSelect
                options={[
                  { value: "client", label: "Client" },
                  { value: "contact", label: "Contact" },
                  { value: "external", label: "External" },
                ]}
                value={form.payeeKind}
                onChange={(val) => {
                  const kind = val || "client";
                  let payeeName = form.payeeName;
                  if (kind === "client" && form.clientId) {
                    const selected = clients.find((c: any) => c._id === form.clientId);
                    if (selected) payeeName = selected.displayName;
                  }
                  setForm({ ...form, payeeKind: kind, payeeName });
                }}
                placeholder="Select type"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Method</label>
              <SearchableSelect
                options={[
                  { value: "eft", label: "EFT" },
                  { value: "wire", label: "Wire" },
                  { value: "cheque", label: "Cheque" },
                ]}
                value={form.routingMethod}
                onChange={(val) => setForm({ ...form, routingMethod: val || "eft" })}
                placeholder="Select method"
              />
            </div>
          </div>
        </form>
      </PremiumModal>
    </div>
  );
};

export default TrustDisbursementsTab;
